import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import {
  AfterContentChecked,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { MatDrawerMode, MatSidenav } from '@angular/material/sidenav';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';

import { AuthenticatorService } from '@aws-amplify/ui-angular';
import { Store } from '@ngrx/store';
import { Subject, Subscription, takeUntil } from 'rxjs';

import { UserService } from '@authentication/services/user.service';
import { LayoutService } from '@layout/containers/layout.service';
import { MerchantSelectors } from '@merchant/store/merchant.slice';
import { hasBanner } from '@shared/interfaces/banner.interface';

@Component({
  selector: 'app-layout-container',
  templateUrl: './layout-container.component.html',
  styleUrls: ['./layout-container.component.scss'],
})
export class LayoutContainerComponent
  implements OnInit, OnDestroy, AfterContentChecked
{
  @ViewChild('sidenavContent') sidenavContent: ElementRef | undefined;
  @ViewChild('sidenav') sidenav: MatSidenav | undefined;
  sidenavHeight = 0;
  screenWidth!: number;

  private destroyed = new Subject<void>();
  private subs = new Subscription();

  roles: string[] = [];

  isLandingPage = false;
  sidenavMode: MatDrawerMode = 'side';
  isSidenavOpen: boolean = this.sidenavMode === 'side';
  footerHeight = 0;
  hasSearchLayout = false;
  hasBanner = false;
  showMenuInFooter = false;

  siteSelected$ = this.store.select(MerchantSelectors.selectIsSiteSelected);

  constructor(
    private breakpointObserver: BreakpointObserver,
    private router: Router,
    private cdr: ChangeDetectorRef,
    public authenticator: AuthenticatorService,
    private layoutService: LayoutService,
    private userService: UserService,
    public host: ElementRef,
    private route: ActivatedRoute,
    private store: Store
  ) {
    this.layoutService.layoutElement = this;
    this.setSidenavMode('');
    this.breakpointObserver
      .observe([Breakpoints.Small])
      .pipe(takeUntil(this.destroyed))
      .subscribe(() => {
        this.setSidenavMode('');
      });

    router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        this.setSidenavMode(event.url);
        setTimeout(() => (this.isSidenavOpen = this.sidenavMode === 'side'));
      }
    });
  }

  setSidenavMode(url: string) {
    if (this.route.snapshot.firstChild?.data['sidenavForceOver']) {
      this.sidenavMode = 'over';
      this.isSidenavOpen = false;
    } else {
      this.sidenavMode =
        this.breakpointObserver.isMatched('(max-width: 892px)') &&
        (this.authenticator.route !== 'authenticated' ||
          this.roles.includes('EASYLOT_ADMIN') ||
          this.roles.includes('TELESALES') ||
          this.roles.includes('IMPERSONATION'))
          ? 'over'
          : 'side';
    }
    setTimeout(() => {
      this.footerHeight =
        document.getElementById('easy-footer')?.getBoundingClientRect()
          .height ?? 0;
    });
  }

  ngOnInit(): void {
    this.hasSearchLayout = findData(this.route);
    this.hasBanner = hasBanner(this.route);
    this.screenWidth = window.innerWidth;
    this.isLandingPage = this.router.url.includes('/home');
    this.subs.add(
      this.router.events.subscribe((e) => {
        if (e instanceof NavigationEnd) {
          this.hasSearchLayout = findData(this.route);
          this.hasBanner = hasBanner(this.route);
        }
        this.isLandingPage =
          e instanceof NavigationEnd && e.url.includes('/home');
      })
    );

    this.subs.add(
      this.userService.currentUserRoles.subscribe((roles) => {
        if (roles && JSON.stringify(roles) !== JSON.stringify(this.roles)) {
          this.roles = roles;
        }
      })
    );
  }

  @HostListener('window:resize', ['$event'])
  onResize(_event: any) {
    this.screenWidth = window.innerWidth;
  }

  @HostListener('window:scroll', ['$event'])
  onWindowScroll(_event: Event) {
    const footer = document.getElementsByTagName('app-footer')[0];
    const footerTop = footer.getBoundingClientRect().top;
    const mobileMenu = document.getElementById('mobile-menu');
    const mobileMenuTop = mobileMenu?.getBoundingClientRect().top ?? 0;
    this.showMenuInFooter = footerTop < mobileMenuTop;
  }

  closeSidenav() {
    this.isSidenavOpen = false;
  }

  ngAfterContentChecked(): void {
    this.cdr.detectChanges();
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
    this.destroyed.next();
    this.destroyed.complete();
  }
}

export function findData(route: ActivatedRoute | null) {
  let isSearchLayout = false;
  route?.children.forEach((routeChild) => {
    if (routeChild.snapshot.data['searchLayout'] === true) {
      isSearchLayout = true;
      return;
    } else {
      isSearchLayout = findData(routeChild);
    }
  });
  return isSearchLayout;
}
